"use client";

import type { JSX } from "react";
import { Icon, Link, Text, View } from "@/components";
import { useLayout } from "@/providers";
import type { VerifyResult } from "@/types/elearning";
import { i18nEnumKey } from "@/utils/elearning/i18nEnumKey";

export interface VerifyResultPanelProps {
  /** Código consultado (se muestra tal cual llegó en la URL/QR). */
  code: string;
  /**
   * Resultado de la verificación pública. `null` indica error de red
   * (se comunica sin distinguir detalles internos).
   */
  result: VerifyResult | null;
}

/**
 * @name VerifyResultPanel
 * @description Panel público de verificación de certificados (P2). Superficie
 * mínima y deep-linkable desde el QR impreso: muestra SOLO los datos visibles que
 * expone el endpoint público (titular, curso, tipo, fechas, estado) y nunca
 * identificadores internos. Un código inválido se comunica como "no válido"
 * sin distinguir el motivo.
 *
 * @param {VerifyResultPanelProps} props - Código y resultado del servidor.
 * @returns {JSX.Element} El panel renderizado.
 */
const VerifyResultPanel = ({
  code,
  result
}: VerifyResultPanelProps): JSX.Element => {
  const { t } = useLayout();

  const renderContent = (): JSX.Element => {
    if (!result?.valid) {
      return (
        <View className="flex flex-col items-center gap-3 text-center">
          <View className="flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 text-destructive">
            <Icon name="fa-solid fa-circle-xmark" className="text-3xl" />
          </View>
          <Text type="h2" className="text-xl font-bold text-foreground">
            {t("certificateNotValid")}
          </Text>
          <Text className="text-sm text-muted-foreground max-w-sm">
            {t("certificateNotValidDetail")}
          </Text>
        </View>
      );
    }

    const revocado = result.status === "revoked";
    const vencido = result.status === "expired";

    return (
      <View className="flex flex-col gap-5">
        <View className="flex flex-col items-center gap-3 text-center">
          <View
            className={`flex items-center justify-center w-16 h-16 rounded-full ${
              revocado
                ? "bg-destructive/10 text-destructive"
                : vencido
                  ? "bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400"
                  : "bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400"
            }`}
          >
            <Icon
              name={
                revocado
                  ? "fa-solid fa-ban"
                  : vencido
                    ? "fa-regular fa-clock"
                    : "fa-solid fa-circle-check"
              }
              className="text-3xl"
            />
          </View>
          <Text type="h2" className="text-xl font-bold text-foreground">
            {t(i18nEnumKey("certificateVerify", result.status ?? "valid"))}
          </Text>
          {revocado && result.revocationReason && (
            <Text className="text-sm text-destructive">
              {`${t("revocationReason")}: ${result.revocationReason}`}
            </Text>
          )}
        </View>

        <View className="flex flex-col gap-3 p-4 rounded-xl bg-muted">
          <View className="flex justify-between gap-4">
            <Text className="text-sm text-muted-foreground">
              {t("certificateHolder")}
            </Text>
            <Text className="text-sm font-medium text-foreground text-right">
              {result.holderName}
            </Text>
          </View>
          {result.holderDocument && (
            <View className="flex justify-between gap-4">
              <Text className="text-sm text-muted-foreground">
                {t("holderDocument")}
              </Text>
              <Text className="text-sm font-medium text-foreground text-right">
                {result.holderDocument}
              </Text>
            </View>
          )}
          <View className="flex justify-between gap-4">
            <Text className="text-sm text-muted-foreground">{t("course")}</Text>
            <Text className="text-sm font-medium text-foreground text-right">
              {result.title}
            </Text>
          </View>
          <View className="flex justify-between gap-4">
            <Text className="text-sm text-muted-foreground">
              {t("issuedAt")}
            </Text>
            <Text className="text-sm font-medium text-foreground text-right">
              {result.issuedAt}
            </Text>
          </View>
          <View className="flex justify-between gap-4">
            <Text className="text-sm text-muted-foreground">
              {t("validUntil")}
            </Text>
            <Text className="text-sm font-medium text-foreground text-right">
              {result.expiresAt}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  return (
    <View
      type="main"
      className="min-h-screen flex items-center justify-center bg-background p-6"
    >
      <View className="box w-full max-w-xl p-8 rounded-3xl border border-border shadow-xl flex flex-col gap-6">
        <View className="flex flex-col items-center gap-1 text-center">
          <Text type="h1" className="text-lg font-semibold text-foreground">
            {t("certificateVerification")}
          </Text>
          <Text className="text-xs text-muted-foreground">
            {`${t("certificateCode")}: ${code}`}
          </Text>
        </View>

        {renderContent()}

        <View className="flex justify-center">
          <Link
            href="/elearning/verificar"
            className="text-sm font-medium text-primary hover:underline"
          >
            {t("verifyAnotherCode")}
          </Link>
        </View>
      </View>
    </View>
  );
};

export default VerifyResultPanel;
